/* eslint-disable react/destructuring-assignment */

import Checkbox from '@mui/material/Checkbox';
import { ChangeEvent } from 'react';
import { Column, Record } from '../types';
import classes from './selectableColumn.module.css';

export function selectableColumn<T extends SelectableRecord>(
  columnConfig: SelectColumn<T> = {} as any,
): SelectColumn<T> {
  return {
    id: 'select',

    isRecordSelected(record: T) {
      return Boolean(record.isSelected);
    },

    isRecordSelectable(record: T) {
      return record.isSelectable !== false;
    },

    setRecordSelected(record: T, value: boolean) {
      if (typeof record.setSelected === 'function') {
        record.setSelected?.(value);
      } else {
        Object.assign(record, { isSelected: value });
      }
    },

    renderCell(record: T, column: SelectColumn<T>) {
      const isSelected = column.isRecordSelected(record);
      const isSelectable = column.isRecordSelectable(record);

      function onToggle(event: ChangeEvent<HTMLInputElement>) {
        const { checked } = event.target;
        column.setRecordSelected(record, checked);
      }

      return (
        <td className={classes.selectCell} key={column.id as string}>
          <Checkbox
            checked={isSelected}
            className={classes.checkbox}
            disabled={!isSelectable}
            onChange={onToggle}
          />
        </td>
      );
    },

    renderHeader(column: SelectColumn<T>, data: T[]) {
      const allSelected = data
        .filter((record) => column.isRecordSelectable(record))
        .every((record) => column.isRecordSelected(record));

      function onToggle(event: ChangeEvent<HTMLInputElement>) {
        const { checked } = event.target;
        data.forEach((record) => column.setRecordSelected(record, checked));
      }

      return (
        <th className={classes.selectCell} key={column.id as string}>
          <Checkbox
            className={classes.checkbox}
            onChange={onToggle}
            value={allSelected}
          />
        </th>
      );
    },

    ...(columnConfig as Partial<SelectColumn<T>>),
  } as SelectColumn<T>;
}

export interface SelectColumn<T> extends Column<T> {
  isRecordSelectable(record: T): boolean;
  isRecordSelected(record: T): boolean;
  setRecordSelected(record: T, value: boolean): void;
}

export interface SelectableRecord extends Record {
  isSelectable?: boolean;
  isSelected?: boolean;
  setSelected?: (value: boolean) => void;
}
